import { Auth } from "aws-amplify";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { Button, InputGroup } from "react-bootstrap";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { toast } from "react-toastify";
import * as yup from "yup";
import FormikCheckbox from "../../components/Forms/FormikCheckbox";
import FormikPasswordField from "../../components/Forms/FormikPasswordField";
import LoaderButton from "../../components/LoaderButton";
import photo1 from "../../images/security.svg";
import FormImage from "../../layouts/formimage";
import { Constants } from "../../libs/Constants";
import { placeEvent } from "../../libs/eventsLib";
import useSignUpContext from "./contexts/useSignUpContext";

const initialValues = {
  password: "",
  acceptTerms: false,
};
const schema = yup.object({
  password: yup
    .string()
    .required("Password is required")
    .min(12, "Must meet all requirements")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!_@#\$%\^&\*])(?=.{8,})/,
      "Must meet all requirements"
    ),
  acceptTerms: yup
    .boolean()
    .required("Please agree to terms and conditions")
    .oneOf([true], "Please agree to terms and conditions"),
});

const SetPassword = ({ onSuccess, onCancel }) => {
  const [visibility, setVisibility] = useState(true);
  const { user, setUser } = useSignUpContext();

  //This method is used to handle different user state
  const handleUserState = async ({ username, password }) => {
    try {
      Auth.configure({ storage: sessionStorage });
      await Auth.signIn(username, password);
    } catch (err) {
      switch (err.code) {
        case "UserNotConfirmedException":
          // resent verification mail
          await Auth.resendSignUp(username);
          onSuccess({
            user: { username, password },
          });
          return false;

        default:
          return true;
      }
    }
    return true;
  };
  const handleSubmit = async (values) => {
    let loginParams = {
      username: user.companyEmail.toLowerCase(),
      attributes: {
        email: user.companyEmail.toLowerCase(),
        given_name: user.firstName,
        family_name: user.lastName,
        zoneinfo: "self",
        "custom:companyid": user.company,
        "custom:joiningdate": user.employmentStartDate,
        birthdate: user.dateOfBirth,
        address: user.address,
        "custom:country": user.country,
        "custom:zip": user.zip,
        "custom:state": user.state,
        "custom:city": user.city,
      },
    };
    let proceed = await handleUserState({ ...loginParams, ...values });
    if (proceed) {
      loginParams = {
        ...loginParams,
        password: values.password,
      };
      try {
        await Auth.signUp(loginParams);
        setUser({ ...user, password: values.password });
        await placeEvent(
          Constants.EVENT_TYPES.PASSWORD,
          user.companyEmail.toLowerCase(),
          {}
        );
        onSuccess();
      } catch (err) {
        if (err && err.message) {
          let { message } = err;
          if (message) {
            message = message.replace("PreSignUp failed with error ", "");
          }
          toast.error(message);
        } else {
          toast.error("Unable to sign up. Please contact admin.");
        }
      }
    }
  };
  return (
    <FormImage
      title="Taking Security Seriously"
      subtitle="We are serious about keeping your information safe and secure."
      image={photo1}
      imageInMobile={false}
      bgClass="background-blue"
    >
      <React.Fragment>
        <h2 className="text-center pb-3">Create An Account</h2>
      </React.Fragment>
      <Formik
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={initialValues}
      >
        {({ isValid, isSubmitting }) => (
          <Form className="w-100">
            <FormikPasswordField
              size="lg"
              controlId="password"
              label="Set Password"
              type={!visibility ? "text" : "password"}
              name="password"
              className="mb-2"
              inputGroupAppend={
                <>
                  {!visibility && (
                    <InputGroup.Text
                      id="basic-addon2"
                      onClick={() => setVisibility(true)}
                    >
                      <MdVisibility />
                    </InputGroup.Text>
                  )}
                  {visibility && (
                    <InputGroup.Text
                      id="basic-addon2"
                      onClick={() => setVisibility(false)}
                    >
                      <MdVisibilityOff />
                    </InputGroup.Text>
                  )}
                </>
              }
            />
            <FormikCheckbox
              controlId="acceptTerms"
              name="acceptTerms"
              className="my-4"
              label={
                <span className="form-label">
                  By checking this box you agree to our
                  <a
                    className="text-primary"
                    href="/termsandconditions"
                    target="_blank"
                  >
                    {" "}
                    Terms of Service and Privacy Policy,{" "}
                  </a>
                  as well as our partner Dwolla’s{" "}
                  <a
                    className="text-primary"
                    href={Constants.DWOLLA_LINKS.TOS}
                    target="_blank"
                  >
                    Terms of Service{" "}
                  </a>
                  and{" "}
                  <a
                    className="text-primary"
                    href={Constants.DWOLLA_LINKS.PRIVACY}
                    target="_blank"
                  >
                    Privacy Policy.
                  </a>
                </span>
              }
            ></FormikCheckbox>
            <LoaderButton
              block
              type="submit"
              variant="primary"
              size="lg"
              text="Next"
              className="mb-2"
              isLoading={isSubmitting}
              disabled={!isValid || isSubmitting}
            >
              Create Account
            </LoaderButton>
          </Form>
        )}
      </Formik>
      <div className="d-flex pb-4 justify-content-center font-secondary account">
        <Button variant="link" onClick={onCancel} className="secondary ps-1">
          {"< Back"}
        </Button>
      </div>
    </FormImage>
  );
};

export default SetPassword;
